import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from './api.js';
import type { CartItem } from './cart.js';

interface CheckoutBody {
  readonly cartItems: ReadonlyArray<{
    readonly skuId: string;
    readonly binding: { readonly type: CartItem['bindingType']; readonly value: string };
    readonly deploymentNotes?: string;
  }>;
}

interface CheckoutResponse {
  readonly orderId: string;
  readonly checkoutUrl: string;
}

export function useCheckout() {
  return useMutation<CheckoutResponse, Error, ReadonlyArray<CartItem>>({
    mutationFn: async (items) => {
      const body: CheckoutBody = {
        cartItems: items.map((i) => ({
          skuId: i.skuId,
          binding: { type: i.bindingType, value: i.bindingValue },
          ...(i.deploymentNotes.trim() ? { deploymentNotes: i.deploymentNotes.trim() } : {})
        }))
      };
      return api.post<CheckoutResponse>('/checkout', body);
    }
  });
}

export interface OrderStatusResponse {
  readonly status: 'pending' | 'paid-but-unfulfilled' | 'fulfilled' | 'cancelled' | 'refunded';
  readonly issuanceIds?: ReadonlyArray<string>;
}

// Polls /api/orders/{id}/status while the order is still working its way
// through the webhook. Stops polling on fulfilled / refunded / cancelled.
export function useOrderStatus(orderId: string | undefined) {
  return useQuery<OrderStatusResponse>({
    queryKey: ['order-status', orderId ?? ''],
    enabled: Boolean(orderId),
    queryFn: () => api.get<OrderStatusResponse>(`/orders/${encodeURIComponent(orderId!)}/status`),
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      if (s === 'fulfilled' || s === 'cancelled' || s === 'refunded') return false;
      return 2000;
    }
  });
}
